import { useEffect, useRef, useState } from 'react';
import { RotateCcw, Settings2 } from 'lucide-react';
import GridLayout, { type Layout } from 'react-grid-layout';
import type { WidgetConfig } from '@contracts';
import { METRIC_CATALOG } from '@contracts';
import { useDashboard, type SaveTarget } from '@/lib/dashboardContext';
import { useFilters } from '@/lib/filterContext';
import { getCompanies, isDashboardAdmin } from '@/services/authService';
import { companiesForMetric, widgetAppliesToCompanies } from '@/lib/companyVisibility';
import { fetchSourceConfig, type SourceConfig } from '@/services/sourceConfigService';
import { SourceConfigPanel } from '@/components/SourceConfigPanel';
import { ScopeSaveModal } from '@/components/ScopeSaveModal';
import { WidgetCard } from '@/widgets/WidgetCard';
import { WidgetWizard } from '@/components/WidgetWizard';
import { DashboardLoadingOverlay } from '@/components/DashboardLoadingOverlay';

/** Métricas vigentes del catálogo (las retiradas se ignoran aunque sigan en tableros guardados). */
const KNOWN_METRICS = new Set<string>(METRIC_CATALOG.map((m) => m.key));

/** Re-empaca los widgets VISIBLES en orden de lectura (fila, luego columna), sin huecos.
 *  Se usa SOLO en modo vista: cuando el filtro de compañía oculta widgets (ej. los Total
 *  Ventas por-empresa con una sola empresa), los visibles se recorren y el acomodo NO queda
 *  disperso. En edición se respeta el acomodo guardado (para arrastrar libremente). */
function packLayout(items: { id: string; w: number; h: number; minW: number; minH: number }[]): Layout[] {
  const out: Layout[] = [];
  let x = 0, y = 0, rowH = 0;
  for (const it of items) {
    if (x + it.w > 12) { x = 0; y += rowH; rowH = 0; } // no cabe en la fila → salto
    out.push({ i: it.id, x, y, w: it.w, h: it.h, minW: it.minW, minH: it.minH });
    x += it.w;
    rowH = Math.max(rowH, it.h);
  }
  return out;
}

/** Tamaño MÍNIMO (cols/filas) por tipo de widget — para que el resize no oculte los datos. */
function minSize(config: WidgetConfig): { minW: number; minH: number } {
  const viz = config.visualization;
  if (viz === 'kpi' || viz === 'gauge') return { minW: 2, minH: 2 };
  if (viz === 'heatmap') return { minW: 5, minH: 3 };
  if (viz === 'donut' || viz === 'pie') return { minW: 3, minH: 4 };
  // bar/line/area/table y rankings (necesitan ancho para etiquetas/toolbar).
  return { minW: 3, minH: 3 };
}

/** Empresas a las que el admin puede asignar este widget como default: las suyas filtradas por
 *  la membresía de la fuente (si el widget es solo de VG, solo VG; custom/sin métrica → todas). */
function selectableCompaniesFor(config: WidgetConfig, overrides: SourceConfig) {
  const admin = getCompanies();
  if (!config.metric) return admin;
  const entry = METRIC_CATALOG.find((m) => m.key === config.metric);
  if (!entry) return admin;
  const member = overrides[config.metric] ?? companiesForMetric(entry);
  return admin.filter((c) => member.includes(c.slug));
}

/** Grid configurable: en modo edición se arrastra/redimensiona y se agregan/editan/quitan widgets. */
export function DashboardPage() {
  const { widgets, editMode, addWidget, updateWidget, removeWidget, resetWidget, hasDefault, setLayouts, resetToDefault } =
    useDashboard();
  const { companies } = useFilters();
  const [wizard, setWizard] = useState<{ id: string | null } | null>(null);
  // Membresía fuente→compañías editada por el admin (⚙). Vacío = todo derivado.
  const [sourceOverrides, setSourceOverrides] = useState<SourceConfig>({});
  const [sourceCfgOpen, setSourceCfgOpen] = useState(false);
  // Admin: tras Guardar en el wizard se pregunta la capa (mi perfil / compañía) para ESE widget.
  const [pendingWidget, setPendingWidget] = useState<{ config: WidgetConfig; id: string | null } | null>(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const doRestore = async (scope?: 'user' | 'company', companyIds?: number[]) => {
    setRestoring(true);
    await resetToDefault(scope, companyIds);
    setRestoring(false);
  };
  useEffect(() => {
    fetchSourceConfig().then(setSourceOverrides);
  }, []);

  // Oculta los widgets no compatibles con la selección (ej. solo SmartBamboo → sin
  // Clientes Registrados / Apartados). Reaparecen al incluir una empresa Odoo.
  // Ignora widgets de métricas retiradas del catálogo (ej. clientes nuevos/cabina) aunque sigan
  // en un tablero guardado → la eliminación es efectiva también en lo ya persistido.
  const visibleWidgets = widgets.filter(
    (w) =>
      (w.config.customQuery || KNOWN_METRICS.has(w.config.metric ?? '')) &&
      widgetAppliesToCompanies(w.config, companies, sourceOverrides),
  );

  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1200));
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // En móvil el grid de 12 columnas no cabe → apilamos en una columna (scroll vertical).
  const isMobile = width < 768;

  // Mínimos de tamaño: el resize no puede bajar de donde los datos se aprecian. Se respeta
  // SIEMPRE el acomodo guardado (RGL compacta vertical por default); no se re-acomoda en vista.
  // Edición: acomodo guardado (arrastre libre). Vista: re-empacado sin huecos (los widgets
  // ocultos por el filtro de compañía no dejan el tablero disperso).
  const layout: Layout[] = editMode
    ? visibleWidgets.map((w) => ({ i: w.id, ...w.layout, ...minSize(w.config) }))
    : packLayout(
        [...visibleWidgets]
          .sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x)
          .map((w) => ({ id: w.id, w: w.layout.w, h: w.layout.h, ...minSize(w.config) })),
      );
  const editingWidget = wizard?.id ? widgets.find((w) => w.id === wizard.id) : null;

  const applyWidget = (config: WidgetConfig, id: string | null, save?: SaveTarget) => {
    if (id) updateWidget(id, config, save);
    else addWidget(config, save);
  };

  const handleSave = (config: WidgetConfig) => {
    const id = wizard?.id ?? null;
    setWizard(null);
    // Admin → preguntar la capa para este widget. Gerente/regional → su vista (al salir de edición).
    if (isDashboardAdmin()) setPendingWidget({ config, id });
    else applyWidget(config, id);
  };

  // Guardar la ayuda editada del popover ⓘ: mismo flujo de scope que editar un widget (admin).
  const saveWidgetHelp = (id: string, help: string) => {
    const w = widgets.find((x) => x.id === id);
    if (!w) return;
    const config = { ...w.config, help };
    if (isDashboardAdmin()) setPendingWidget({ config, id });
    else applyWidget(config, id);
  };

  const renderCard = (w: (typeof visibleWidgets)[number]) => (
    <WidgetCard
      widget={w}
      editMode={editMode}
      onEdit={() => setWizard({ id: w.id })}
      onRemove={() => removeWidget(w.id)}
      onResetConfig={() => resetWidget(w.id)}
      canReset={hasDefault(w.id)}
      onSaveHelp={saveWidgetHelp}
    />
  );

  // Alto del widget en móvil. Los charts (ECharts height:100%) NECESITAN alto definido;
  // los 'split' (Más/Menos vendidos) crecen a su contenido (auto) para no scrollear.
  const mobileHeight = (w: (typeof visibleWidgets)[number]): number | undefined => {
    const viz = w.config.visualization;
    // split y donut/pie crecen a su contenido (donut arriba + leyenda HTML paginada). El resto fijo.
    if (viz === 'split' || viz === 'donut' || viz === 'pie') return undefined;
    return w.layout.h * 60 + (w.layout.h - 1) * 16;
  };

  return (
    <div ref={ref}>
      <DashboardLoadingOverlay />
      {editMode && (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          {/* Agregar widgets personalizados: SOLO admins del dashboard. */}
          {isDashboardAdmin() && (
            <button
              onClick={() => setWizard({ id: null })}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              + Agregar widget
            </button>
          )}
          <button
            disabled={restoring}
            onClick={() => {
              // Admin → modal (mi tablero / el de las compañías). Gerente/regional → directo (su vista).
              if (isDashboardAdmin()) {
                setRestoreOpen(true);
              } else if (window.confirm('¿Restaurar tu tablero? Vuelve al default y se quitan tus cambios.')) {
                doRestore();
              }
            }}
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-red-300 hover:text-red-600 disabled:opacity-60"
          >
            <RotateCcw className={`h-4 w-4 ${restoring ? 'animate-spin' : ''}`} />
            {restoring ? 'Restaurando…' : 'Restaurar tablero'}
          </button>
          {/* Membresía fuente→compañía (qué fuentes aparecen por empresa). Solo admin. */}
          {isDashboardAdmin() && (
            <button
              onClick={() => setSourceCfgOpen(true)}
              className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-red-300 hover:text-red-600"
            >
              <Settings2 className="h-4 w-4" /> Fuentes por empresa
            </button>
          )}
          <span className="text-xs text-slate-500">
            {isMobile ? (
              <>Edita o quita cada widget con sus botones (el reacomodo es desde escritorio)</>
            ) : (
              <>Arrastra cualquier widget para moverlo · usa la <strong>esquina inferior derecha</strong> para redimensionar</>
            )}
          </span>
        </div>
      )}

      {isMobile ? (
        // Apilado en 1 columna, en el orden de lectura del grid (fila, luego columna).
        <div className="flex flex-col gap-4">
          {[...visibleWidgets]
            .sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x)
            .map((w) => {
              const h = mobileHeight(w);
              return (
                <div key={w.id} style={h != null ? { height: h } : undefined}>
                  {renderCard(w)}
                </div>
              );
            })}
        </div>
      ) : (
        <GridLayout
          className={`layout ${editMode ? 'editing' : ''}`}
          layout={layout}
          cols={12}
          rowHeight={60}
          width={width}
          margin={[16, 16]}
          isDraggable={editMode}
          isResizable={editMode}
          draggableCancel=".no-drag"
          onDragStop={(l) => setLayouts(l)}
          onResizeStop={(l) => setLayouts(l)}
        >
          {visibleWidgets.map((w) => (
            <div key={w.id}>{renderCard(w)}</div>
          ))}
        </GridLayout>
      )}

      {wizard && (
        <WidgetWizard
          initial={editingWidget?.config ?? null}
          companies={companies}
          sourceOverrides={sourceOverrides}
          onSave={handleSave}
          onClose={() => setWizard(null)}
        />
      )}
      {sourceCfgOpen && (
        <SourceConfigPanel
          overrides={sourceOverrides}
          onSaved={(cfg) => {
            setSourceOverrides(cfg);
            setSourceCfgOpen(false);
          }}
          onClose={() => setSourceCfgOpen(false)}
        />
      )}
      {pendingWidget && (
        <ScopeSaveModal
          companies={selectableCompaniesFor(pendingWidget.config, sourceOverrides)}
          onConfirm={(scope, companyIds) => {
            applyWidget(pendingWidget.config, pendingWidget.id, { scope, companyIds });
            setPendingWidget(null);
          }}
          onClose={() => setPendingWidget(null)}
        />
      )}
      {restoreOpen && (
        <ScopeSaveModal
          variant="restore"
          companies={getCompanies()}
          onConfirm={(scope, companyIds) => {
            setRestoreOpen(false);
            doRestore(scope, companyIds);
          }}
          onClose={() => setRestoreOpen(false)}
        />
      )}
    </div>
  );
}
